import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChatCircleDots, Cube, Receipt, Key, ArrowRight, Lightning, Brain } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const cards = [
  { to: "/dashboard/workspace", icon: ChatCircleDots, title: "AI Workspace", desc: "Chat across 10+ models with Instant→Ultra effort levels.", testid: "card-workspace" },
  { to: "/dashboard/builder", icon: Cube, title: "App Builder", desc: "Describe an app → get generated code + live spec.", testid: "card-builder" },
  { to: "/dashboard/taxos", icon: Receipt, title: "TaxOS", desc: "ITR / GST / TDS engines, compliance & filings.", testid: "card-taxos" },
  { to: "/dashboard/api", icon: Key, title: "Universal API", desc: "Your key, usage metering and developer docs.", testid: "card-api" },
];

export default function Overview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [iq, setIq] = useState(null);

  useEffect(() => {
    api.get("/apikey").then((r) => setStats(r.data)).catch(() => {});
    api.get("/intelligence").then((r) => setIq(r.data)).catch(() => {});
  }, []);

  return (
    <div className="p-8 lg:p-10 max-w-6xl">
      <div className="h-16 flex items-center -mt-4 mb-2" />
      <div className="z-rise">
        <p className="text-sm text-[#8C8381]">Welcome back</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">
          Hello, {user?.name?.split(" ")[0] || "there"}.
        </h1>
        <p className="text-[#594F4D] mt-2 max-w-xl">Pick a module to get moving — everything you generate is yours to own and export.</p>
      </div>

      {/* stat pills */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
        {[
          { label: "Total API calls", value: stats?.total_calls ?? "—" },
          { label: "Chat sessions", value: stats?.chat_calls ?? "—" },
          { label: "Apps generated", value: stats?.build_calls ?? "—" },
          { label: "Credits left", value: typeof stats?.credits_remaining === "number" ? stats.credits_remaining.toLocaleString() : (stats?.credits_remaining ?? "—") },
        ].map((s) => (
          <div key={s.label} className="bg-white border border-[#2A2625]/10 rounded-2xl p-5">
            <p className="text-2xl font-display font-semibold">{s.value}</p>
            <p className="text-xs text-[#8C8381] mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mt-6">
        {cards.map((c) => (
          <button
            key={c.to}
            data-testid={c.testid}
            onClick={() => navigate(c.to)}
            className="group text-left bg-white border border-[#2A2625]/10 rounded-2xl p-7 hover:-translate-y-1 hover:shadow-[0_12px_48px_rgba(42,38,37,0.08)] transition-[transform,box-shadow] duration-300"
          >
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 rounded-2xl bg-[#FFF4F0] flex items-center justify-center">
                <c.icon size={26} weight="duotone" className="text-[#F29A86]" />
              </div>
              <ArrowRight size={20} className="text-[#8C8381] group-hover:translate-x-1 group-hover:text-[#2A2625] transition-transform" />
            </div>
            <h3 className="font-display text-xl font-semibold mt-5">{c.title}</h3>
            <p className="text-[#594F4D] text-sm mt-1.5">{c.desc}</p>
          </button>
        ))}
      </div>

      <div className="mt-6 bg-[#2A2625] text-white rounded-2xl p-7 flex items-center gap-4">
        <Lightning size={26} weight="fill" className="text-[#FFB8A5] shrink-0" />
        <p className="text-sm text-white/80">Every module runs on your <span className="text-[#FFCBBF] font-medium">Zeyrodha Universal API key</span> — one key, 10+ models, metered billing.</p>
      </div>

      {/* Self-Learning Intelligence */}
      <div className="mt-6 bg-white border border-[#2A2625]/10 rounded-2xl p-7" data-testid="intelligence-panel">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#FFF4F0] flex items-center justify-center"><Brain size={24} weight="duotone" className="text-[#F29A86]" /></div>
          <div>
            <h3 className="font-display text-xl font-semibold">Self-Learning Intelligence</h3>
            <p className="text-sm text-[#594F4D]">Extended reasoning that improves from your feedback</p>
          </div>
          <div className="ml-auto text-right">
            <p className="text-3xl font-display font-semibold text-[#F29A86]" data-testid="iq-score">{iq?.iq_score ?? "—"}</p>
            <p className="text-xs text-[#8C8381]">Intelligence index</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mt-6">
          {(iq?.capabilities || []).map((c) => (
            <div key={c.name}>
              <div className="flex justify-between text-sm mb-1"><span className="text-[#594F4D]">{c.name}</span><span className="font-medium">{c.level}%</span></div>
              <div className="h-2 rounded-full bg-[#2A2625]/8 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-[#FFB8A5] to-[#F29A86] transition-all duration-700" style={{ width: `${c.level}%` }} /></div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-4 mt-6 text-xs text-[#8C8381]">
          <span>Interactions learned: <b className="text-[#2A2625]">{iq?.interactions ?? 0}</b></span>
          <span>👍 {iq?.feedback_up ?? 0}</span>
          <span>👎 {iq?.feedback_down ?? 0}</span>
          <span>Use <b className="text-[#2A2625]">Ultra</b> effort in Workspace for extended step-by-step thinking</span>
        </div>
      </div>
    </div>
  );
}
